import React from 'react';
import { styled } from '@mui/material';
import { Column } from 'further-ui/components/Layout';
import { AllocationRule } from 'further-types/lp/allocations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { IncomeAndExpensesLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { useFormContext } from 'react-hook-form';
import { UpdateCapitalActionFormValues } from '../../../hooks/useCapitalActionForm';
import CapitalCallAllocations from '../CapitalCallAllocations';
import useActionAllocationsChangeHandler from './hooks/useActionAllocationsChangeHandler';

type Expense = {
  id: string;
  type?: string;
  counterpartyLabel: string;
  uncalledAmountLcy: number;
  date: CalendarDay;
};

type Props = {
  vehicleId: string;
  entryIndex: number;
  baseCurrency: CurrencyCode;
  expenses: Array<Expense>;
};

const AllocationsWrapper = styled(Column)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const ExpensesDetails: React.FC<Props> = ({
  expenses,
  baseCurrency,
  vehicleId,
  entryIndex,
}) => {
  const { watch } = useFormContext<UpdateCapitalActionFormValues>();
  const actions = watch(`entries.${entryIndex}.actions`);
  const handleAllocationsChange = useActionAllocationsChangeHandler(entryIndex);

  const { expandedRows, toggleRowExpansion } = useExpandedRows([]);

  const columns: Array<TableColumn<Expense>> = [
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      render: (expense) => (
        <ColorIndicatorWithText
          text={expense.counterpartyLabel}
          colorKey={{ name: expense.counterpartyLabel }}
        />
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (expense) =>
        (expense.type && IncomeAndExpensesLabels[expense.type]) ?? expense.type,
    },
    {
      key: 'date',
      label: 'Date',
      render: (expense) => expense.date.toReadable(),
    },
    {
      key: 'uncalledAmountLcy',
      label: 'Amount',
      render: (expense) =>
        numberToCurrencyString(expense.uncalledAmountLcy, {
          currency: baseCurrency,
        }),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={expenses}
      isExpandableRow={() => true}
      isExpandedRow={({ id }) => expandedRows.has(id)}
      onRowExpand={({ id }) => toggleRowExpansion(id)}
      onRowClick={({ id }) => toggleRowExpansion(id)}
      getExpandedContent={(expense) => (
        <AllocationsWrapper>
          <CapitalCallAllocations
            entryIndex={entryIndex}
            calledFundsLabel="Expense capital call"
            currency={baseCurrency}
            actionId={expense.id}
            allocationRule={AllocationRule.LinkedAction}
            capitalCalledLcy={expense.uncalledAmountLcy}
            vehicleId={vehicleId}
            capitalBreakdown={
              actions.find((action) => action.id === expense.id)
                ?.capitalBreakdown ?? []
            }
            keepCallAmountFixed
            onChange={(capitalBreakdown) => {
              handleAllocationsChange(expense.id, capitalBreakdown);
            }}
          />
        </AllocationsWrapper>
      )}
    />
  );
};

export default ExpensesDetails;
